import { useCallback, useState } from "react";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useRegistrationApiClient } from "@api/registration-api/use-registration-api-client";
import { FaroButton } from "@components/common/faro-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { CaptureTreeEntity } from "@faro-lotv/service-wires";
import { fetchCaptureTreeData } from "@hooks/data-management/use-data-management";
import { useToast } from "@hooks/use-toast";
import { TableItem } from "@pages/data-management/data-management-types";
import { getDeviceType, getScanEntitiesToRemove } from "@pages/data-management/data-management-utils";
import { deleteCaptureTreeEntities } from "@pages/data-management/import-data/cancel-import-utils";
import { startRegistration } from "@pages/data-management/register-data-utils";
import { supportedDeviceTypes } from "@pages/data-management/uploaded-data/uploaded-data-types";
import { captureTreeSelector } from "@store/capture-tree/capture-tree-selectors";
import { useAppSelector, useAppDispatch } from "@store/store-helper";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props extends BaseProjectIdProps {
  /** Button caption. */
  caption: string;
  /** Array of DataManagementTableItem entities. */
  items: TableItem[];
  /** Optional callback when the button is clicked, called before the main action. */
  onClickBefore?: () => void;
}

/** Button to continue with registration with the successful scans after some scans failed to process. */
export function ContinueWithProcessedScansButton({
  projectId,
  caption,
  items,
  onClickBefore,
}: Props): JSX.Element{
  const { trackEvent } = useTrackEvent();
  const { showToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const dispatch = useAppDispatch();
  const treeEntities = useAppSelector(captureTreeSelector);
  const projectApiClient = useProjectApiClient({ projectId });
  const progressApiClient = useProgressApiClient({ projectId });
  const registrationApiClient = useRegistrationApiClient({ projectId });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onContinueProcessing = useCallback(async(): Promise<void> => {
    setIsLoading(true);
    let hasOperationFailed = false;
    let treeEntitiesToRemove: CaptureTreeEntity[] | undefined;

    try {
      if (onClickBefore) {
        onClickBefore();
      }

      const scans = items.filter((item) => item.type === "Scan");
      const scansToRemove = scans.filter((item) => item.state === "error");
      treeEntitiesToRemove = getScanEntitiesToRemove(scansToRemove, treeEntities);
      // It's unexpected if there are no scans to remove, but in that case, there's nothing to do.
      if (treeEntitiesToRemove.length) {
        try {
          await deleteCaptureTreeEntities(projectApiClient, treeEntitiesToRemove);
        } catch (error) {
          hasOperationFailed = true;
          handleErrorWithToast({
            id: `onContinueProcessing-${new Date().toString()}`,
            error,
            title: "Failed to remove corrupt scans. Please reload the page and try again.",
          });
          return;
        }
      }

      const unsupportedScans = scans.filter((item) => !supportedDeviceTypes.includes(getDeviceType(item.deviceType)));
      // Don't start the registration if there are no scans remaining.
      const hasRemainingScans = (scansToRemove.length + unsupportedScans.length) < scans.length;
      if (hasRemainingScans) {
        await startRegistration({
          progressApiClient,
          projectApiClient,
          registrationApiClient,
          trackEvent,
          showToast,
          dispatch,
          handleErrorWithToast,
        });
      } else {
        showToast({
          message: "There were no usable scans. Please retry the upload.",
          type: "info",
          shouldAutoHide: false,
        });
      }
    } catch (error) {
      hasOperationFailed = true;
      throw error;
    } finally {
      trackEvent({
        name: DataManagementEvents.removeScans,
        props: {
          numberOfEntities: treeEntitiesToRemove?.length ?? 0,
          source: "continueWithSuccessfulScans",
          hasOperationFailed,
        },
      });

      // Fetch the new state immediately instead of waiting until the next usePolling() happens.
      try {
        await fetchCaptureTreeData(dispatch, projectApiClient, progressApiClient);
      } catch { /* Not essential. The UI will update a bit later. */ }

      setIsLoading(false);
    }
  }, [
    dispatch,
    handleErrorWithToast,
    items,
    onClickBefore,
    progressApiClient,
    projectApiClient,
    registrationApiClient,
    showToast,
    trackEvent,
    treeEntities,
  ]);

  return (
    <SphereTooltip title="Continue with Processed Scans">
      <FaroButton
        dataTestId="sa-button-continue-process"
        isLoading={isLoading}
        onClick={onContinueProcessing}
      >
        {caption}
      </FaroButton>
    </SphereTooltip>
  );
}
